import { selectEmployees } from '../../employees/reducer';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  selectLastFourHoursTasks,
  selectTodaysTaskCompletions,
  selectCurrentEmployee,
} from '../reducer';
import { TaskListItem } from '../components/TaskListItem';
import { TaskCompletion, Task } from '../models';
import { useState, useEffect } from 'react';
import { getLastFourHoursTasks, getTodaysTaskCompletions } from '../actions';
import { getTodaysTaskCompletionsDefaultQuery } from '../helpers';
import { getApiStatus } from '../../../core/utils';
import Loader from '../../../core/components/Loader';
import HourlyChecklist from '../components/HourlyChecklist';
import { HourlyViewMode } from '../components/HourlyChecklist';
import { fetchEmployees } from '../../employees/actions';
import { FilterSection } from '../../../core/components/FilterSection';
import { EmployeeDropdownAndAddButton } from '../../../core/components/EmployeeDropdownAndAddButton';
import { Drawer } from '../../../core/components/Drawer';
import { cloneDeep } from 'lodash';
import { selectSite } from '../../../store/authReducer';
import { TaskDetails } from '../components/TaskDetails';
import { TASK_DRAWER_WIDTH } from '../types';
import moment from 'moment';
import { BsSun, BsCalendar2, BsMoon, BsCheck2 } from 'react-icons/bs';
import { CgLogIn } from 'react-icons/cg';

const TodaysTasksNew = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const employees = useSelector(selectEmployees);
  const primarySite = useSelector(selectSite);
  const tasks = useSelector(selectTodaysTaskCompletions);
  const [localTasks, setLocalTasks] = useState<TaskCompletion[]>([]);
  const loadingTasks = useSelector((state) => getApiStatus(state, 'GET_TODAYS_TASK_COMPLETIONS'));
  const [hourlyViewMode, setHourlyViewMode] = useState<HourlyViewMode>(HourlyViewMode.Collapsed);
  const globalEmployee = useSelector(selectCurrentEmployee);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const lastFourHoursTasks = useSelector(selectLastFourHoursTasks);
  const today = moment().startOf('day');

  const buildQuery = () => {
    return {
      ...getTodaysTaskCompletionsDefaultQuery(true),
      type: '',
    };
  };

  // on mount and primary site change, get the tasks
  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(getLastFourHoursTasks());
    dispatch(getTodaysTaskCompletions(buildQuery()));
  }, [primarySite]);

  // if we get updated completions, replace local state (hopefully they match)
  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  const isTaskWithType = (tc: TaskCompletion, type: string) => {
    return tc.task?.type === type;
  };

  const sortByName = (a: TaskCompletion, b: TaskCompletion) => {
    return a.task.name.localeCompare(b.task.name);
  };

  const sortComplete = (a: TaskCompletion, b: TaskCompletion) => {
    return new Date(b.timestamp ?? '').getTime() - new Date(a.timestamp ?? '').getTime();
  };

  const filterComplete = (taskList: TaskCompletion[], invert = false) => {
    if (invert) {
      return taskList.filter((task: TaskCompletion) => task.status !== 'Completed');
    } else {
      // Sort completed tasks by completion date
      return taskList
        .filter((task: TaskCompletion) => task.status === 'Completed')
        .sort((a: TaskCompletion, b: TaskCompletion) => {
          return new Date(b.timestamp ?? '').getTime() - new Date(a.timestamp ?? '').getTime();
        });
    }
  };

  const filterRecentTasks = (taskList: TaskCompletion[]) => {
    return taskList.filter(
      (task: TaskCompletion) =>
        task.status !== 'Completed' ||
        (task.status === 'Completed' && task.timestamp && task.timestamp >= today.toISOString())
    );
  };

  const filterDaily = (taskList: TaskCompletion[]) => {
    return filterRecentTasks(
      taskList.filter((task: TaskCompletion) => task.task?.interval !== 'Hourly')
    );
  };

  const filterHourly = (taskList: TaskCompletion[]) => {
    return taskList.filter((task: TaskCompletion) => task.task?.interval === 'Hourly');
  };

  const hasInteriorTasks = (taskList: TaskCompletion[]) => {
    const filtered = taskList.filter(
      (task: TaskCompletion) => task.task?.type === 'ExpressInterior'
    );
    return filtered && filtered.length > 0;
  };

  const fakeComplete = (task: TaskCompletion, empId: number) => {
    const alteredTask = cloneDeep(task);
    const isComplete = task.status == 'Completed';
    alteredTask.employeeId = isComplete ? null : empId ?? globalEmployee;
    alteredTask.timestamp = new Date().toISOString();
    alteredTask.status = isComplete ? 'Pending' : 'Completed';
    return alteredTask;
  };

  const toggleTaskComplete = (taskId: number | string, empId: number) => {
    const modifiedTasks = [...localTasks];
    const indexById = modifiedTasks.findIndex(
      (t: TaskCompletion) => Number(t.id) === Number(taskId)
    );
    if (indexById !== -1) {
      const tempTask = modifiedTasks[indexById];
      modifiedTasks[indexById] = fakeComplete(tempTask, empId);
    }
    setLocalTasks(modifiedTasks);
  };

  const renderTask = (taskCompletion: TaskCompletion, i: number) => {
    return (
      <TaskListItem
        taskCompletion={taskCompletion}
        toggleCompletion={toggleTaskComplete}
        displayOptionsMenu={false}
        displayTimeInfo={false}
        onOpen={(t: any) => setSelectedTask(t?.task)}
        onToggleSnooze={() => getTodaysTaskCompletions(buildQuery())}
        className="tabular"
      />
    );
  };

  return (
    <div className="todays-tasks-new">
      {employees && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {hourlyViewMode === HourlyViewMode.Collapsed && (
              <button
                className="task-drawer-toggle-button"
                onClick={() => setHourlyViewMode(HourlyViewMode.Default)}
              >
                <i className="uil uil-angle-left" />
              </button>
            )}
          </div>
          <div
            className={`tasks-columns${
              hourlyViewMode === HourlyViewMode.Collapsed ? ' hourly-collapsed' : ''
            }`}
          >
            <div className="tasks-daily">
              {loadingTasks ? (
                <Loader />
              ) : (
                <div className="tasks-daily-table">
                  {/* Opening Daily tasks */}
                  <FilterSection
                    itemList={filterDaily(localTasks)}
                    renderFunction={renderTask}
                    filterFunction={(t: TaskCompletion) =>
                      isTaskWithType(t, 'OpenChecklist') && t.status !== 'Completed'
                    }
                    sortFunction={sortByName}
                    title={intl.formatMessage({ id: 'opening' })}
                    icon={<BsSun />}
                  />
                  {/* "Daily" Daily tasks */}
                  <FilterSection
                    itemList={filterDaily(localTasks)}
                    renderFunction={renderTask}
                    filterFunction={(t: TaskCompletion) =>
                      isTaskWithType(t, 'CleaningList') && t.status !== 'Completed'
                    }
                    sortFunction={sortByName}
                    title={intl.formatMessage({ id: 'daily' })}
                    icon={<BsCalendar2 />}
                  />
                  {/* Closing Daily tasks */}
                  <FilterSection
                    itemList={filterDaily(localTasks)}
                    renderFunction={renderTask}
                    filterFunction={(t: TaskCompletion) =>
                      isTaskWithType(t, 'ClosingChecklist') && t.status !== 'Completed'
                    }
                    sortFunction={sortByName}
                    title={intl.formatMessage({ id: 'closing' })}
                    icon={<BsMoon />}
                  />
                  {/* Show filter section if Interior tasks exist */}
                  {hasInteriorTasks(tasks) && (
                    <FilterSection
                      itemList={filterDaily(localTasks)}
                      renderFunction={renderTask}
                      filterFunction={(t: TaskCompletion) =>
                        isTaskWithType(t, 'ExpressInterior') && t.status !== 'Completed'
                      }
                      sortFunction={sortByName}
                      title={intl.formatMessage({ id: 'interior' })}
                      icon={<CgLogIn />}
                      isOpen={false}
                    />
                  )}
                  {/* Completed tasks */}
                  <FilterSection
                    itemList={filterDaily(localTasks)}
                    renderFunction={renderTask}
                    filterFunction={(t: TaskCompletion) => t.status === 'Completed'}
                    sortFunction={sortComplete}
                    title={intl.formatMessage({ id: 'completed' })}
                    icon={<BsCheck2 />}
                    isOpen={false}
                  />
                </div>
              )}
            </div>
            <HourlyChecklist
              hourlyTasks={filterHourly(localTasks)}
              lastFourHoursTasks={lastFourHoursTasks}
              filterComplete={filterComplete}
              toggleTaskComplete={toggleTaskComplete}
              viewMode={hourlyViewMode}
              viewModeChange={setHourlyViewMode}
              setSelectedTask={(t: any) => setSelectedTask(t?.task)}
            />
          </div>
          <Drawer
            expanded={!!selectedTask}
            maximized={false}
            expandedWidth={TASK_DRAWER_WIDTH}
            overlay
            onBackdropClick={() => {
              setSelectedTask(null);
            }}
          >
            {selectedTask ? (
              <TaskDetails
                taskId={selectedTask?.id}
                onClose={() => setSelectedTask(null)}
                onSave={() => getTodaysTaskCompletions(buildQuery())}
                onDelete={() => getTodaysTaskCompletions(buildQuery())}
              />
            ) : (
              ''
            )}
          </Drawer>
        </>
      )}
    </div>
  );
};

export default TodaysTasksNew;
